import { Form, Input, Button, Row, Col, Space, Select, Checkbox } from "antd";

import {
  ReloadOutlined,
  LoadingOutlined,
  CopyOutlined,
} from "@ant-design/icons";
import { Sparkles, Landmark } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { FetchBankAccounts_api } from "../../Api/Bank/bank_api";
import { trimPriceAmount } from "../../lib/common";
import { useEffect } from "react";

const { Option } = Select;

export const PurchaseOrderForm = ({
  maximumAmount,
  formData,
  isAdding,
  handleSubmit,
  handleReset,
  copyToClipboard,
  handleFile,
  imageMetaData,
  isLoading,
  isBatapola,
  documentCharge,
}: any) => {
  const fields = [
    { name: "po_number", label: "PO Number", type: "text" },
    { name: "facility_no", label: "Facility No", type: "text" },
    { name: "issued_by", label: "Issued By", type: "text" },
    { name: "issued_date", label: "Issued Date", type: "text" },
    { name: "seller_name", label: "Seller Name", type: "text" },
    { name: "seller_address", label: "Seller Address", type: "text" },
    { name: "buyer_name", label: "Buyer Name", type: "text" },
    { name: "buyer_address", label: "Buyer Address", type: "text" },
    { name: "item_description", label: "Item Description", type: "text" },
    { name: "equipment_no", label: "Equipment No", type: "text" },
    { name: "vehicle_class", label: "Class of Vehicle", type: "text" },
    { name: "engine_no", label: "Engine No", type: "text" },
    { name: "chassis_no", label: "Chassis No", type: "text" },
    { name: "reg_no", label: "Reg No", type: "text" },
    { name: "model", label: "Model", type: "text" },
    { name: "purchase_price", label: "Purchase Price" },
    { name: "delivery_location", label: "Delivery Location", type: "text" },
    {
      name: "expected_delivery_date",
      label: "Expected Delivery Date",
      type: "text",
    },
  ];

  const requiredFields = [
    "po_number",
    "issued_by",
    "seller_name",
    "buyer_name",
    "item_description",
    "bank_account_id",
    "purchase_price",
    "issued_date",
  ];

  const amountValue = Form.useWatch("purchase_price", formData);
  const approvedLeaseAmount = Form.useWatch("approved_lease_amount", formData);

  useEffect(() => {
    if (isBatapola && approvedLeaseAmount !== undefined) {
      const docCharge = parseFloat(documentCharge || 0);
      const approved = parseFloat(approvedLeaseAmount || 0);
      const netDisbursement = Math.max(0, approved - docCharge);
      formData.setFieldsValue({
        purchase_price: trimPriceAmount(netDisbursement),
      });
    }
  }, [approvedLeaseAmount, isBatapola, documentCharge, formData]);

  const { data: bankAccounts, isFetching: isFetchingBanks } = useQuery({
    queryKey: ["bankAccounts"],
    queryFn: async () => {
      const res = await FetchBankAccounts_api();
      return res?.data || [];
    },
    refetchOnWindowFocus: false,
  });

  return (
    <Form
      form={formData}
      layout="vertical"
      onFinish={(value) => {
        handleSubmit(value);
      }}
      initialValues={formData}
      className="p-4 rounded-lg"
    >
      <div className="bg-blue-50 p-4 rounded-xl border border-blue-100 mb-6 flex items-start gap-3">
        <Landmark className="text-blue-500 w-5 h-5 mt-1" />
        <div className="flex-1">
          <Form.Item
            label={
              <span className="font-semibold text-blue-700">
                Deposited to (Bank Account)
              </span>
            }
            name="bank_account_id"
            rules={[
              {
                required: true,
                message: "Please select the destination bank account",
              },
            ]}
            className="mb-0"
          >
            <Select
              placeholder="-- Select Internal Bank Account --"
              loading={isFetchingBanks}
              className="w-full"
            >
              {bankAccounts
                ?.filter((acc: any) => acc.isOwn == 1)
                ?.map((acc: any) => (
                  <Option key={acc.id} value={acc.id}>
                    {acc.bank_name} - {acc.account_name} ({acc.account_number})
                  </Option>
                ))}
            </Select>
          </Form.Item>
        </div>
      </div>

      {isBatapola && (
        <div className="bg-orange-50 p-4 rounded-xl border border-orange-100 mb-6">
          <Row gutter={[16, 16]}>
            <Col xs={24} md={12}>
              <Form.Item
                label={
                  <span className="font-semibold text-orange-700">
                    Approved Lease Amount (LKR)
                  </span>
                }
                name="approved_lease_amount"
                rules={[
                  {
                    required: true,
                    message: "Please enter approved lease amount",
                  },
                ]}
                normalize={(value) =>
                  trimPriceAmount(Number(value.toString().replace(/,/g, "")))
                }
              >
                <Input placeholder="Enter approved lease amount" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <div className="flex flex-col">
                <span className="text-sm font-semibold text-orange-700 mb-1">
                  DOC Charge (Deduction)
                </span>
                <div className="bg-white border border-orange-200 rounded px-3 py-1.5 font-medium text-red-500">
                  Rs. {parseFloat(documentCharge || 0).toLocaleString()}
                </div>
              </div>
            </Col>
          </Row>
          <div className="mt-3 text-sm text-orange-600 font-medium italic">
            Note: Net Disbursement (Purchase Price) = Approved Lease Amount -
            DOC Charge
          </div>
        </div>
      )}

      <Row gutter={[16, 16]}>
        {fields.map((field: any) => {
          const isRequired = requiredFields.includes(field.name);
          const isAmount = field.name === "purchase_price";
          const maximumAmountRule = isAmount
            ? [
                {
                  validator: (_: any, value: any) => {
                    if (value && Number(value) > maximumAmount) {
                      return Promise.reject(
                        `Amount cannot be greater than ${maximumAmount}`,
                      );
                    }
                    return Promise.resolve();
                  },
                },
              ]
            : [];
          return (
            <Col xs={24} md={12} key={field.name}>
              <Form.Item
                className="my-0"
                label={field.label}
                name={field.name}
                normalize={
                  isAmount
                    ? (value) =>
                        trimPriceAmount(
                          Number(value.toString().replace(/,/g, "")),
                        )
                    : undefined
                }
                rules={
                  isRequired
                    ? [
                        {
                          required: true,
                          message: `Please enter ${field.label}`,
                        },
                        ...maximumAmountRule,
                      ]
                    : []
                }
              >
                <Input
                  type={field.type}
                  placeholder={`Enter ${field.label}`}
                  name={field.name}
                  value={formData[field.name]}
                  readOnly={isBatapola && isAmount}
                  className={isBatapola && isAmount ? "bg-gray-100" : ""}
                />
              </Form.Item>
            </Col>
          );
        })}

        <Col xs={24} md={12}>
          <Form.Item
            className="my-0"
            label="Is credited"
            name="is_credited"
            valuePropName="checked"
          >
            <Checkbox />
          </Form.Item>
        </Col>

        {/* Other IDs */}
        {formData.other_ids?.length > 0 && (
          <Form.Item label="Other IDs" className="mt-5">
            <Space wrap>
              {formData.other_ids.map((id: string, idx: number) => (
                <Button
                  key={idx}
                  type="default"
                  onClick={() => copyToClipboard(id)}
                  icon={<CopyOutlined />}
                >
                  {id}
                </Button>
              ))}
            </Space>
          </Form.Item>
        )}
      </Row>

      <Form.Item>
        <Space className="w-full justify-between flex mt-16">
          <Button
            loading={isLoading}
            className="flex items-center gap-2 rounded-2xl bg-gradient-to-r from-blue-600 via-cyan-500 to-sky-400 px-6 py-3 text-white font-semibold shadow-md transition-all duration-300 hover:from-cyan-500 hover:to-blue-600"
            htmlType="button"
            disabled={!imageMetaData}
            onClick={() => {
              handleFile(imageMetaData);
            }}
            icon={<Sparkles />}
          >
            Re annelize
          </Button>
          <div className="flex space-x-2">
            <Button
              htmlType="button"
              onClick={handleReset}
              icon={<ReloadOutlined />}
            >
              Reset
            </Button>
            <Button
              disabled={
                isLoading ||
                maximumAmount === 0 ||
                (amountValue && Number(amountValue) > maximumAmount)
              }
              type="primary"
              htmlType="submit"
              loading={isAdding}
              icon={isAdding ? <LoadingOutlined /> : undefined}
            >
              Submit
            </Button>
          </div>
        </Space>
      </Form.Item>
    </Form>
  );
};
